import React, { useState, useEffect } from "react";
import {
  Box,
  Tab,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
} from "@material-ui/core";
import TabList from "@material-ui/lab/TabList";
import TabPanel from "@material-ui/lab/TabPanel";
import TabContext from "@material-ui/lab/TabContext";
import DashboardRoundedIcon from "@material-ui/icons/DashboardRounded";
import AssignmentRoundedIcon from "@material-ui/icons/AssignmentRounded";
import LocalHospitalRoundedIcon from "@material-ui/icons/LocalHospitalRounded";
import SpaRoundedIcon from "@material-ui/icons/SpaRounded";
import EditRoundedIcon from "@material-ui/icons/EditRounded";
import AssessmentParameters, {
  ASSESSMENT_PARAMS,
} from "./AssessmentParameters";
import IndriyaParameters, { INDRIYA_ORGANS } from "./IndriyaParameters";
import AssessmentModulePopup from "./AssessmentModulePopup";
import { POPUP_MODULES } from "./assessmentModuleForms";
import NadiReading from "./NadiReading";
import PatientAnswers from "../components/PatientAnswers";
import RecommendationDisplay from "../components/RecommendationDisplay";
import MedicineMatrix from "./MedicineMatrix";
import HerbalMedicineProtocols from "./HerbalMedicineProtocols";
import ClinicalSummary from "./ClinicalSummary";
import ReportsLauncher from "./ReportsLauncher";
import PresentingComplaint from "./PresentingComplaint";
import ChatbotSummary from "./ChatbotSummary";
import HomeRemedies from "./HomeRemedies";
import { connect } from "react-redux";
import useCommonStyles from "./style";
import usePatientData from "./usePatientData";
import {
  getHealthScoreRequest,
  getPatientImageAnalysisStore,
  getPatientQuestionAnswer,
  getPatientsSymptom,
  getPhysicalAssessmentRequest,
  savePhysicalAssessmentRequest,
  getNadiReportRequest,
  saveNadiReportRequest,
  createNadiReportRequest,
  getClinicalBloodReportRequest,
  getChatbotReportsRequest,
  getAssessmentSessionRequest,
  saveAssessmentSessionRequest,
  regenerateAssessmentReportRequest,
  getAssessmentComprehensiveReportRequest,
} from "../../../PatientApiActions";

// Symptom Analyzer reports newest-first (by report_date).
const symptomReportsDesc = (reports) =>
  (Array.isArray(reports) ? reports : [])
    .filter((r) => r && r.report_type === "symptom_analyzer" && r.payload)
    .sort((a, b) =>
      String(b.report_date || "").localeCompare(String(a.report_date || ""))
    );

// Latest chatbot report payload of a given report_type (by report_date).
const latestChatbotPayload = (reports, reportType) => {
  const picked = (Array.isArray(reports) ? reports : [])
    .filter((r) => r && r.report_type === reportType && r.payload)
    .sort((a, b) =>
      String(b.report_date || "").localeCompare(String(a.report_date || ""))
    )[0];
  return picked ? picked.payload : null;
};

// A food_based_support block has content when the remedy or the trigger pattern
// is populated (the chatbot sometimes fills only one).
const hasRemedyContent = (fbs) => {
  const rec = (fbs && fbs.recommendation) || {};
  const trig = (fbs && fbs.trigger_pattern) || {};
  return !!(
    rec.title ||
    rec.preparation ||
    rec.meaning ||
    rec.traditional_note ||
    trig.title ||
    trig.description
  );
};

// The 5 most recent Symptom Analyzer reports that actually carry a home remedy,
// as { date, support } — newest first. Date prefers the payload's assessment_date.
const latestHomeRemedies = (reports, limit = 5) =>
  symptomReportsDesc(reports)
    .filter((r) => hasRemedyContent(r.payload.food_based_support))
    .slice(0, limit)
    .map((r) => ({
      date:
        (r.payload.patient && r.payload.patient.assessment_date) ||
        r.report_date ||
        "",
      support: r.payload.food_based_support,
    }));

// Module-scope helpers (stable component types so reused children — e.g.
// ClinicalSummary, BloodReportDashboard — are NOT remounted on re-render).

const SectionCard = ({ classes, title, accent, editable, children, style }) => (
  <Box className={classes.hiSectionCard} style={style}>
    <Box className={classes.hiSectionHeader}>
      <span
        className={classes.hiSectionDot}
        style={accent ? { backgroundColor: accent } : undefined}
      />
      <Typography className={classes.hiSectionTitle}>{title}</Typography>
      {editable && (
        <EditRoundedIcon className={classes.hiEditIcon} titleAccess="Editable" />
      )}
    </Box>
    <Box>{children}</Box>
  </Box>
);

// Family-history questions live inside the Medical History question set. They
// are pulled out of "Clinical Medical History" and shown under "Family History"
// instead. Module-level so the array reference stays stable across renders
// (PatientAnswers refetches when these props change).
const FAMILY_HISTORY_MATCH = ["first degree relatives"];

// The display value for a module from its saved session result: classification
// carries the value under type | label | pattern (which one depends on module).
const classificationValue = (moduleResult) => {
  const cls = moduleResult && moduleResult.classification;
  if (!cls) return null;
  return cls.type || cls.label || cls.pattern || null;
};

const HealthInformationTabs = ({
  valueHealthHistory,
  handleChangeHealthHistory,
  patient,
  caseId,
  getHealthScore,
  getPatientsSymptom,
  getPatientImageAnalysisStore,
  getPatientQuestionAnswer,
  getPhysicalAssessment,
  savePhysicalAssessment,
  getNadiReport,
  saveNadiReport,
  createNadiReport,
  getClinicalBloodReport,
  getChatbotReports,
  getAssessmentSession,
  saveAssessmentSession,
  regenerateAssessmentReport,
  getAssessmentComprehensiveReport,
}) => {
  const classes = useCommonStyles();

  // Doctor-selected Ayurvedic assessment values (dropdown label per module key).
  // Seeded from the saved assessment session and persisted back via the Save
  // button. Shared with the Overview tab (reflects the selections).
  const [assessment, setAssessment] = useState({});
  // Indriya is per-organ: maps each organ key to its selected dosha ("" = none).
  const [indriya, setIndriya] = useState({});
  // True while the comprehensive report is rebuilding (slow, ~30-70s).
  const [regenerating, setRegenerating] = useState(false);
  // The assessment param whose question popup is open (null = closed).
  const [openModule, setOpenModule] = useState(null);
  // The raw session module_results from the last GET, kept so a Save can PUT back
  // the full set (preserving modules/fields the portal doesn't manage).
  const [assessmentModuleResults, setAssessmentModuleResults] = useState({});
  // Free-text notes/feedback the doctor can write in the Recommendations tab.
  // Local state only — persistence pending a backend endpoint.
  const [feedbackNote, setFeedbackNote] = useState("");
  // Doctor-entered physical parameters (local state only — persistence pending
  // a backend endpoint).
  const [physical, setPhysical] = useState({
    appetite: "",
    bowel: "",
    bowelFrequency: "",
    micturition: "",
    micturitionDay: "",
    micturitionNight: "",
    sleep: "",
  });
  const setPhysicalField = (key, value) =>
    setPhysical((prev) => ({ ...prev, [key]: value }));

  // Load the saved Physical Assessment for this case from Strapi on open.
  // Leaves the form empty if no record exists.
  useEffect(() => {
    if (!caseId) return;
    getPhysicalAssessment(caseId).then((record) => {
      if (!record) return;
      const a = record.attributes || record;
      setPhysical({
        appetite: a.appetite || "",
        bowel: a.bowel || "",
        bowelFrequency: a.bowel_frequency || "",
        micturition: a.micturition || "",
        micturitionDay:
          a.daytime_frequency != null ? String(a.daytime_frequency) : "",
        micturitionNight:
          a.nighttime_frequency != null ? String(a.nighttime_frequency) : "",
        sleep: a.sleep || "",
      });
    });
  }, [caseId]);

  // Build the Strapi payload, normalising empty values to null (invalid enum /
  // number values are never sent) and frequencies to numbers.
  const handleSavePhysical = () => {
    if (!caseId) return;
    const orNull = (v) => (v === "" || v === undefined ? null : v);
    const numOrNull = (v) => {
      if (v === "" || v === undefined || v === null) return null;
      const n = Number(v);
      return Number.isNaN(n) ? null : n;
    };
    const payload = {
      user_id: patient && patient.patientId ? String(patient.patientId) : "",
      case_id: String(caseId),
      appetite: orNull(physical.appetite),
      bowel: orNull(physical.bowel),
      bowel_frequency: orNull(physical.bowelFrequency),
      micturition: orNull(physical.micturition),
      daytime_frequency: numOrNull(physical.micturitionDay),
      nighttime_frequency: numOrNull(physical.micturitionNight),
      sleep: orNull(physical.sleep),
    };
    savePhysicalAssessment(caseId, payload).catch(() => {});
  };

  // --- Nadi Reading (pulse-derived vata/pitta/kapha, editable & persisted) ---
  // Nadi reports are keyed on the patient's plain 10-digit phone; patient.mobile
  // carries a country-code prefix, so take the last 10 digits to match.
  const mobileDigits = (patient?.mobile || "").replace(/\D/g, "");
  const patientPhone =
    mobileDigits.length > 10 ? mobileDigits.slice(-10) : mobileDigits;

  const [nadi, setNadi] = useState({ vata: "", pitta: "", kapha: "" });
  // Strapi id of the loaded Nadi report (needed for the update) and whether the
  // doctor has changed a value since load/save (gates the Save button + PUT).
  const [nadiRecordId, setNadiRecordId] = useState(null);
  const [nadiDirty, setNadiDirty] = useState(false);

  // Load the latest Nadi report for this patient. Leaves the section in its
  // "no report" state when none exists.
  useEffect(() => {
    if (!patientPhone) return;
    getNadiReport(patientPhone).then((record) => {
      if (!record) {
        setNadiRecordId(null);
        return;
      }
      // Strapi v4 nests fields under `attributes`; v5 keeps them flat.
      const a = record.attributes || record;
      const asStr = (v) => (v === null || v === undefined ? "" : String(v));
      setNadiRecordId(record.documentId || record.id);
      setNadi({
        vata: asStr(a.vata),
        pitta: asStr(a.pitta),
        kapha: asStr(a.kapha),
      });
      setNadiDirty(false);
    });
  }, [patientPhone]);

  const setNadiField = (key, value) => {
    setNadi((prev) => ({ ...prev, [key]: value }));
    setNadiDirty(true);
  };

  // Persist the edited vata/pitta/kapha. With no report for this patient yet
  // the reading is recorded on a new record, so the doctor can enter it before
  // a device report exists. Blank fields are normalised to null; non-blank
  // values are sent as numbers.
  const handleSaveNadi = () => {
    const numOrNull = (v) => {
      if (v === "" || v === undefined || v === null) return null;
      const n = Number(v);
      return Number.isNaN(n) ? null : n;
    };
    const payload = {
      vata: numOrNull(nadi.vata),
      pitta: numOrNull(nadi.pitta),
      kapha: numOrNull(nadi.kapha),
    };
    if (nadiRecordId) {
      saveNadiReport(nadiRecordId, payload)
        .then(() => setNadiDirty(false))
        .catch(() => {});
      return;
    }
    if (!patientPhone) return;
    createNadiReport(patientPhone, payload)
      .then((record) => {
        // Keep editing the record just created instead of adding another.
        if (record) setNadiRecordId(record.documentId || record.id);
        setNadiDirty(false);
      })
      .catch(() => {});
  };

  // --- Key Risk Flags & Lab Indicators (from the Clinical Blood Report) ---
  // AI-highlighted lab findings, read-only. Keyed on the same patient phone.
  const [labFindings, setLabFindings] = useState(null);
  useEffect(() => {
    if (!patientPhone) return;
    getClinicalBloodReport(patientPhone).then((record) => {
      if (!record) {
        setLabFindings(null);
        return;
      }
      // Strapi v4 nests fields under `attributes`; v5 keeps them flat.
      const a = record.attributes || record;
      setLabFindings(Array.isArray(a.labFindings) ? a.labFindings : null);
    });
  }, [patientPhone]);

  const [symptomSummary, setSymptomSummary] = useState(null);
  const [homeRemedies, setHomeRemedies] = useState([]);
  const [assessmentSummary, setAssessmentSummary] = useState(null);
  useEffect(() => {
    if (!patientPhone) return;
    let active = true;
    getChatbotReports(patientPhone)
      .then((reports) => {
        if (!active) return;
        const symptom = latestChatbotPayload(reports, "symptom_analyzer");
        setSymptomSummary(symptom ? symptom.clinical_summary : null);
        setHomeRemedies(latestHomeRemedies(reports));
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [getChatbotReports, patientPhone]);

  useEffect(() => {
    if (!patientPhone) return;
    let active = true;
    getAssessmentComprehensiveReport(patientPhone)
      .then((payload) => {
        if (active) setAssessmentSummary(payload);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [getAssessmentComprehensiveReport, patientPhone]);

  // A module's questions were edited & recomputed in the popup (calculate also
  // persisted to the session): update the raw result + the dropdown value.
  const handleModuleSaved = (moduleId, result) => {
    if (!result) return;
    setAssessmentModuleResults((prev) => ({ ...prev, [moduleId]: result }));
    // indriya is per-organ: refresh each organ's dosha from the new result.
    if (moduleId === "indriya") {
      const organResults = result.organ_results || {};
      setIndriya((prev) => {
        const next = { ...prev };
        INDRIYA_ORGANS.forEach((o) => {
          const r = organResults[o.key];
          if (r && r.dosha) next[o.key] = r.dosha;
        });
        return next;
      });
      return;
    }
    const param = ASSESSMENT_PARAMS.find((p) => p.moduleId === moduleId);
    if (param) {
      const val = classificationValue(result);
      if (val) setAssessment((prev) => ({ ...prev, [param.key]: val }));
    }
  };

  // Load the patient's saved assessment session and seed the dropdowns: the 11
  // single-classification modules + the per-organ indriya. Keeps the raw
  // module_results for saving.
  useEffect(() => {
    if (!patientPhone) return;
    let active = true;
    getAssessmentSession(patientPhone)
      .then((session) => {
        if (!active) return;
        const moduleResults = (session && session.module_results) || {};
        setAssessmentModuleResults(moduleResults);
        const seed = {};
        ASSESSMENT_PARAMS.forEach((p) => {
          const val = classificationValue(moduleResults[p.moduleId]);
          if (val) seed[p.key] = val;
        });
        setAssessment(seed);
        // indriya — seed each organ's dosha from organ_results.
        const organResults =
          (moduleResults.indriya && moduleResults.indriya.organ_results) || {};
        const indriyaSeed = {};
        INDRIYA_ORGANS.forEach((o) => {
          const r = organResults[o.key];
          if (r && r.dosha) indriyaSeed[o.key] = r.dosha;
        });
        setIndriya(indriyaSeed);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [getAssessmentSession, patientPhone]);

  // Build the full module_results to PUT: start from the raw session, override
  // each changed module with a fresh classification, and leave everything else
  // (scores/messages, unmanaged modules) intact.
  const buildModuleResults = () => {
    const moduleResults = JSON.parse(
      JSON.stringify(assessmentModuleResults || {})
    );

    ASSESSMENT_PARAMS.forEach((p) => {
      const value = assessment[p.key];
      if (!value) return;
      const existing = moduleResults[p.moduleId];
      // Unchanged — keep the original module result (scores/message) intact.
      if (existing && classificationValue(existing) === value) return;
      const opt = (p.options || []).find((o) => o.label === value);
      const classification = { [p.field]: value };
      if (opt && opt.id) classification.id = opt.id;
      // Override only the classification — the module's saved answers
      // (selected_* / answers, scores, flags) must survive, otherwise the row
      // keeps showing a result while its questions popup comes up empty.
      moduleResults[p.moduleId] = { ...(existing || {}), classification };
    });

    // indriya — rebuild organ_results from the doctor's picks (preserving an
    // organ's original data when unchanged) and recompute the dosha tally.
    const DOSHA_KEY = { Vāta: "V", Pitta: "P", Kapha: "K" };
    const origIndriya =
      (assessmentModuleResults && assessmentModuleResults.indriya) || {};
    const origOrgans = origIndriya.organ_results || {};
    const anyIndriya = INDRIYA_ORGANS.some((o) => indriya[o.key]);
    if (anyIndriya || Object.keys(origIndriya).length) {
      const organResults = {};
      const tally = { V: 0, P: 0, K: 0 };
      INDRIYA_ORGANS.forEach((o) => {
        const dosha = indriya[o.key];
        if (!dosha) {
          organResults[o.key] = null;
          return;
        }
        const prev = origOrgans[o.key];
        let entry;
        if (prev && prev.dosha === dosha) {
          entry = prev; // unchanged — keep scores/message/treatment
        } else {
          const k = DOSHA_KEY[dosha];
          entry = { dosha, label: `${dosha} ${o.short}`, scores: { V: 0, P: 0, K: 0 } };
          if (k) entry.scores[k] = 1;
        }
        organResults[o.key] = entry;
        const s = entry.scores || {};
        tally.V += s.V || 0;
        tally.P += s.P || 0;
        tally.K += s.K || 0;
      });
      moduleResults.indriya = {
        ...origIndriya,
        organ_results: organResults,
        overall_dosha_tally: tally,
      };
    }

    return moduleResults;
  };

  // Save the selections (PUT only — does not rebuild the report).
  const handleSaveAssessment = () => {
    if (!patientPhone) return;
    saveAssessmentSession(patientPhone, {
      module_results: buildModuleResults(),
    }).catch(() => {});
  };

  const handleRegenerate = () => {
    if (!patientPhone || regenerating) return;
    setRegenerating(true);
    saveAssessmentSession(patientPhone, { module_results: buildModuleResults() })
      .then(() => regenerateAssessmentReport(patientPhone))
      .then(() => getAssessmentComprehensiveReport(patientPhone))
      .then((payload) => {
        setAssessmentSummary(payload);
        setRegenerating(false);
      })
      .catch(() => setRegenerating(false));
  };

  const {
    recommendations,
    imageAnalysisReportList,
    filteredRecommendation,
  } = usePatientData(
    patient,
    getHealthScore,
    getPatientsSymptom,
    getPatientImageAnalysisStore,
    getPatientQuestionAnswer
  );

  const tabs = [
    { value: "1", label: "Overview", icon: <DashboardRoundedIcon /> },
    { value: "2", label: "Assessment", icon: <AssignmentRoundedIcon /> },
    {
      value: "3",
      label: "Reports",
      icon: <LocalHospitalRoundedIcon />,
    },
    { value: "4", label: "Recommendations", icon: <SpaRoundedIcon /> },
  ];

  return (
    <Box className={classes.hiContainer}>
      <TabContext value={valueHealthHistory}>
        <TabList
          className={classes.hiTabList}
          onChange={handleChangeHealthHistory}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          TabIndicatorProps={{ style: { display: "none" } }}
        >
          {tabs.map((t) => (
            <Tab
              key={t.value}
              value={t.value}
              label={t.label}
              icon={t.icon}
              className={classes.hiTab}
            />
          ))}
        </TabList>

        <TabPanel value="1" className={classes.hiPanel}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <SectionCard
                classes={classes}
                title="Presenting Complaint"
                style={{ height: 200, marginBottom: 15, overflowY: "auto" }}
              >
                <PresentingComplaint summary={symptomSummary} />
              </SectionCard>
            </Grid>
            <Grid item xs={12} md={8}>
              <SectionCard
                classes={classes}
                title="Physical Parameters"
                style={{ height: 200, marginBottom: 15 }}
              >
                <Grid container spacing={3} style={{ marginTop: 6 }}>
                  {/* Appetite */}
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center">
                      <Typography
                        className={classes.assessmentLabel}
                        style={{ fontWeight: 700, minWidth: 92, marginBottom: 0 }}
                      >
                        Appetite
                      </Typography>
                      <FormControl variant="outlined" size="small" style={{ flex: 1 }}>
                        <Select
                          value={physical.appetite}
                          displayEmpty
                          onChange={(e) =>
                            setPhysicalField("appetite", e.target.value)
                          }
                        >
                          <MenuItem value="">
                            <em>Select</em>
                          </MenuItem>
                          {["Normal", "Increased", "Irregular", "Poor"].map((o) => (
                            <MenuItem key={o} value={o}>
                              {o}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Grid>

                  {/* Bowel */}
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center">
                      <Typography
                        className={classes.assessmentLabel}
                        style={{ fontWeight: 700, minWidth: 64, marginBottom: 0 }}
                      >
                        Bowel
                      </Typography>
                      <FormControl
                        variant="outlined"
                        size="small"
                        style={{ flex: 1, marginRight: 8 }}
                      >
                        <Select
                          value={physical.bowel}
                          displayEmpty
                          onChange={(e) =>
                            setPhysicalField("bowel", e.target.value)
                          }
                        >
                          <MenuItem value="">
                            <em>Select</em>
                          </MenuItem>
                          {[
                            "Normal",
                            "Constipation",
                            "Loose Stools",
                            "Irregular",
                          ].map((o) => (
                            <MenuItem key={o} value={o}>
                              {o}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl variant="outlined" size="small" style={{ flex: 1 }}>
                        <Select
                          value={physical.bowelFrequency}
                          displayEmpty
                          onChange={(e) =>
                            setPhysicalField("bowelFrequency", e.target.value)
                          }
                        >
                          <MenuItem value="">
                            <em>Frequency</em>
                          </MenuItem>
                          {["Once", "Twice"].map((o) => (
                            <MenuItem key={o} value={o}>
                              {o}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Grid>

                  {/* Micturition */}
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center">
                      <Typography
                        className={classes.assessmentLabel}
                        style={{ fontWeight: 700, minWidth: 92, marginBottom: 0 }}
                      >
                        Micturition
                      </Typography>
                      <FormControl variant="outlined" size="small" style={{ flex: 1 }}>
                        <Select
                          value={physical.micturition}
                          displayEmpty
                          onChange={(e) =>
                            setPhysicalField("micturition", e.target.value)
                          }
                        >
                          <MenuItem value="">
                            <em>Select</em>
                          </MenuItem>
                          {[
                            "Normal",
                            "Increased Frequency",
                            "Reduced Frequency",
                            "Burning Urination",
                            "Nocturia",
                            "Incomplete Emptying",
                          ].map((o) => (
                            <MenuItem key={o} value={o}>
                              {o}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    <Box display="flex" mt={1} style={{ marginLeft: 92 }}>
                      <TextField
                        type="number"
                        size="small"
                        variant="outlined"
                        label="times/day"
                        InputLabelProps={{ shrink: true }}
                        value={physical.micturitionDay}
                        onChange={(e) =>
                          setPhysicalField("micturitionDay", e.target.value)
                        }
                        style={{ flex: 1, marginRight: 8 }}
                      />
                      <TextField
                        type="number"
                        size="small"
                        variant="outlined"
                        label="times/night"
                        InputLabelProps={{ shrink: true }}
                        value={physical.micturitionNight}
                        onChange={(e) =>
                          setPhysicalField("micturitionNight", e.target.value)
                        }
                        style={{ flex: 1 }}
                      />
                    </Box>
                  </Grid>

                  {/* Sleep */}
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center">
                      <Typography
                        className={classes.assessmentLabel}
                        style={{ fontWeight: 700, minWidth: 64, marginBottom: 0 }}
                      >
                        Sleep
                      </Typography>
                      <FormControl variant="outlined" size="small" style={{ flex: 1 }}>
                        <Select
                          value={physical.sleep}
                          displayEmpty
                          onChange={(e) =>
                            setPhysicalField("sleep", e.target.value)
                          }
                        >
                          <MenuItem value="">
                            <em>Select</em>
                          </MenuItem>
                          {[
                            "Good",
                            "Disturbed",
                            "Difficulty Falling Asleep",
                            "Excessive Sleep",
                            "Non-Refreshing Sleep",
                            "Insomnia",
                          ].map((o) => (
                            <MenuItem key={o} value={o}>
                              {o}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Grid>
                </Grid>

                <Box display="flex" justifyContent="flex-end" mt={-5}>
                  <Button
                    variant="contained"
                    color="primary"
                    disableElevation
                    className={classes.hiSaveBtn}
                    onClick={handleSavePhysical}
                  >
                    Save
                  </Button>
                </Box>
              </SectionCard>
            </Grid>
          </Grid>

          <Box className={classes.hiSectionCard}>
            <ClinicalSummary
              patientId={patient?.patientId}
              selectedValues={assessment}
              labFindings={labFindings}
            />
          </Box>

        </TabPanel>

        <TabPanel value="2" className={classes.hiPanel}>
          <SectionCard classes={classes} title="Assessment Parameters" editable>
            <AssessmentParameters
              values={assessment}
              onOpenModule={setOpenModule}
              openableModuleIds={POPUP_MODULES}
            />
            <IndriyaParameters values={indriya} onOpenModule={setOpenModule} />
            <Box
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
              mt={2}
              style={{ gap: 12 }}
            >
              <Button
                variant="outlined"
                disabled={regenerating}
                className={classes.hlFullBtn}
                startIcon={
                  regenerating ? <CircularProgress size={16} /> : undefined
                }
                onClick={handleRegenerate}
              >
                {regenerating ? "Regenerating…" : "Regenerate report"}
              </Button>
              <Button
                variant="contained"
                color="primary"
                disableElevation
                disabled={regenerating}
                className={classes.hiSaveBtn}
                onClick={handleSaveAssessment}
              >
                Save
              </Button>
            </Box>
          </SectionCard>

          <AssessmentModulePopup
            open={!!openModule}
            moduleId={openModule && openModule.moduleId}
            moduleLabel={openModule && openModule.label}
            phone={patientPhone}
            sessionModule={
              openModule
                ? assessmentModuleResults[openModule.moduleId]
                : null
            }
            onClose={() => setOpenModule(null)}
            onSaved={handleModuleSaved}
          />

          <SectionCard classes={classes} title="Vikruti Reading" editable>
            <NadiReading
              values={nadi}
              onChange={setNadiField}
              onSave={handleSaveNadi}
              dirty={nadiDirty}
            />
          </SectionCard>

          <SectionCard classes={classes} title="Clinical Medical History">
            <PatientAnswers
              key="history1"
              type="Medical History 1"
              patientId={patient?.patientId}
              excludeQuestions={FAMILY_HISTORY_MATCH}
              emptyMessage="No medical history found in section 1."
            />
            <PatientAnswers
              key="history2"
              type="Medical History 2"
              patientId={patient?.patientId}
              excludeQuestions={FAMILY_HISTORY_MATCH}
              emptyMessage="No medical history found in section 2."
            />
          </SectionCard>

          <SectionCard classes={classes} title="Family History">
            <PatientAnswers
              key="family1"
              type="Medical History 1"
              patientId={patient?.patientId}
              matchQuestions={FAMILY_HISTORY_MATCH}
              hideWhenEmpty
            />
            <PatientAnswers
              key="family2"
              type="Medical History 2"
              patientId={patient?.patientId}
              matchQuestions={FAMILY_HISTORY_MATCH}
              hideWhenEmpty
            />
          </SectionCard>

          <SectionCard classes={classes} title="Long Chatbot Output Summary">
            <ChatbotSummary payload={assessmentSummary} />
          </SectionCard>
        </TabPanel>

        <TabPanel value="3" className={classes.hiPanel}>
          <ReportsLauncher
            patient={patient}
            imageAnalysisReportList={imageAnalysisReportList}
          />
        </TabPanel>

        <TabPanel value="4" className={classes.hiPanel}>
          

          <SectionCard
            classes={classes}
            title="AI Recommendations (Health Score)"
          >
            <RecommendationDisplay
              recommendations={recommendations}
              filteredRecommendation={filteredRecommendation}
            />
          </SectionCard>

          <SectionCard
            classes={classes}
            title="Diet, Lifestyle, Dinacharya, Herbal & Medicine Protocols"
          >
            <HerbalMedicineProtocols payload={assessmentSummary} />
          </SectionCard>

          <SectionCard
            classes={classes}
            title="Medicine Matrix"
            accent="#0a8f54"
          >
            <MedicineMatrix />
          </SectionCard>
          <SectionCard classes={classes} title="Short Chatbot Home Remedies">
            <HomeRemedies remedies={homeRemedies} />
          </SectionCard>

          <SectionCard classes={classes} title="Notes / Feedback" editable>
            <TextField
              fullWidth
              multiline
              minRows={4}
              variant="outlined"
              placeholder="Write your notes or feedback here…"
              value={feedbackNote}
              onChange={(e) => setFeedbackNote(e.target.value)}
            />
          </SectionCard>
        </TabPanel>
      </TabContext>
    </Box>
  );
};

const mapStateToProps = (state) => {
  return {
    patientSymptom: state.patientSymptom,
    patientQuestionAnswer: state.patientQuestionAnswer,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getHealthScore: (mobileNumber) => {
      return dispatch(getHealthScoreRequest(mobileNumber)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },

    getPatientsSymptom: (patientId) => {
      return dispatch(getPatientsSymptom(patientId)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    getPatientImageAnalysisStore: (patientId) => {
      return dispatch(getPatientImageAnalysisStore(patientId)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    getPatientQuestionAnswer: (type, userId) => {
      return dispatch(getPatientQuestionAnswer(type, userId)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    getPhysicalAssessment: (caseId) => {
      return dispatch(getPhysicalAssessmentRequest(caseId)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    savePhysicalAssessment: (caseId, payload) => {
      return dispatch(savePhysicalAssessmentRequest(caseId, payload)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    getNadiReport: (phone) => {
      return dispatch(getNadiReportRequest(phone)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    saveNadiReport: (recordId, payload) => {
      return dispatch(saveNadiReportRequest(recordId, payload)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    createNadiReport: (phone, payload) => {
      return dispatch(createNadiReportRequest(phone, payload)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    getClinicalBloodReport: (phone) => {
      return dispatch(getClinicalBloodReportRequest(phone)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    getChatbotReports: (phone) => {
      return dispatch(getChatbotReportsRequest(phone)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    getAssessmentSession: (phone) => {
      return dispatch(getAssessmentSessionRequest(phone)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    saveAssessmentSession: (phone, body) => {
      return dispatch(saveAssessmentSessionRequest(phone, body)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    regenerateAssessmentReport: (phone) => {
      return dispatch(regenerateAssessmentReportRequest(phone)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    getAssessmentComprehensiveReport: (phone) => {
      return dispatch(getAssessmentComprehensiveReportRequest(phone)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
  };
};
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(HealthInformationTabs);
